/**
 * CreditCost — the « ~3 crédits » badge shown BEFORE an AI action.
 *
 * Render it as a SIBLING right next to an AI button — never inside it (the
 * button's accessible name must stay its own label). Prefer {@link WithCreditCost}:
 *
 *   <WithCreditCost feature="generate_content"><Button>Générer</Button></WithCreditCost>
 *
 * The number comes from `GET /api/v1/credits/costs` (server-authoritative, the
 * same price the gate requires and the meter reserves) — never hard-coded. It
 * renders nothing until the price list is known or when the tenant is unlimited,
 * so it can never show a wrong number. `features` sums a multi-step action.
 */
import type { ReactNode } from 'react';

import { useT } from '@/i18n';
import { useFeatureCost, useFeatureCostSum } from './useCreditCosts';
import { useCreditsStore } from './useCreditsStore';
import { isUnlimitedWallet } from '@/lib/api/credits';

function fmt(n: number): string {
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

export function CreditCostLabel({ cost }: { cost: number }) {
  const { t } = useT();
  const n = fmt(cost);
  const text = t(cost < 2 ? 'shell.features.creditsUsage.costOne' : 'shell.features.creditsUsage.costMany', { n });
  return (
    <span
      data-testid="credit-cost"
      data-cost={cost}
      title={t('shell.features.creditsUsage.costTitle', { n })}
      className="inline-flex items-center whitespace-nowrap rounded-full border border-border bg-surface-2/60 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted"
    >
      {text}
    </span>
  );
}

export function CreditCost({
  feature,
  features,
  className = '',
}: {
  /** Single feature key (see GET /api/v1/credits/costs). */
  feature?: string;
  /** Multi-step action: the badge shows the sum of these features' prices. */
  features?: readonly string[];
  className?: string;
}) {
  const single = useFeatureCost(feature);
  const sum = useFeatureCostSum(features ?? []);
  const wallet = useCreditsStore((s) => s.wallet);
  const cost = features ? sum : single;
  if (cost == null || cost <= 0) return null;
  // Unlimited accounts spend nothing - no price tag.
  if (isUnlimitedWallet(wallet)) return null;
  return (
    <span className={className}>
      <CreditCostLabel cost={cost} />
    </span>
  );
}

/**
 * WithCreditCost — wrap an AI button so its « ~N crédits » badge sits RIGHT NEXT
 * to it (a sibling, never inside the button: the button's accessible name stays
 * exactly its own label).
 *
 *   <WithCreditCost feature="content_ideas">
 *     <Button onClick={run}>{t('…generateIdeas')}</Button>
 *   </WithCreditCost>
 *
 * `block` makes the wrapper a full-width flex row (give the button `flex-1`).
 */
export function WithCreditCost({
  feature,
  features,
  children,
  className = '',
  block = false,
}: {
  feature?: string;
  features?: readonly string[];
  children: ReactNode;
  className?: string;
  block?: boolean;
}) {
  const key = features ? features.join('+') : (feature ?? '');
  return (
    <span
      data-testid="credit-action"
      data-feature={key}
      className={`${block ? 'flex w-full' : 'inline-flex'} items-center gap-1.5 ${className}`.trim()}
    >
      {children}
      <CreditCost feature={feature} features={features} />
    </span>
  );
}
